import { AccountMenu } from "@/components/layout/AccountMenu";
import { ClipboardSidebarSections } from "@/components/layout/ClipboardSidebarSections";
import { DocsSidebarSection } from "@/components/layout/DocsSidebarSection";
import { useTreeViewOutletContext } from "@/components/layout/TreeViewOutletContext";
import { Avatar } from "@/components/ui/Avatar";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { ChatSessionList } from "@/features/chat/components/ChatSessionList";
import { showRepositories } from "@/features/config/featureFlags";
import { useFeatureFlags } from "@/features/config/useFeatureFlags";
import { IS_DEV } from "@/features/config";
import { CurationSessionList } from "@/features/data-curation/components/CurationSessionList";
import { OldDatasetsLink } from "@/features/data-curation/components/OldDatasetsLink";
import {
  DATASETS_ROUTE,
  datasetsAccess,
  LEGACY_DATASETS_ROUTE,
} from "@/features/datasets/routes";
import { FAST_ROUTE, FastDatasetList } from "@/features/fast-dataset/components/FastDatasetList";
import { RepositoriesSidebarSection } from "@/features/repos/components/RepositoriesSidebarSection";
import { ThemeToggle } from "@/features/theme/ThemeToggle";
import { useUsageWarnings } from "@/features/usage/hooks/useUsageWarnings";
import { useNavItems } from "@/hooks/useNavItems";
import { cn } from "@/utils/cn";
import { closeLeftSidebarAtom } from "@/utils/sidebarStore";
import { useSetAtom } from "jotai";
import { ChevronsUpDown, Plus } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

type NavItem = {
  to: string;
  label: string;
  beta?: boolean;
  /** Extra path prefixes that should also mark this item active. */
  matchPrefixes?: string[];
  /** Local-dev-only destination; shows a developer marker. */
  devOnly?: boolean;
};

/**
 * Usage notice in the rail footer: a full-bleed warn/danger bar that links to
 * Settings when a quota is near or at its limit.
 */
const UsageRailNote = ({ onNavigate }: { onNavigate: (to: string) => void }) => {
  const warnings = useUsageWarnings();
  if (warnings.length === 0) return null;

  const reached = warnings.filter((w) => w.isAtLimit).length;
  const approaching = warnings.filter((w) => w.isApproachingLimit).length;
  const primary = warnings[0];
  const atLimit = reached > 0;
  const label =
    reached > 1
      ? `${reached} limits reached`
      : reached === 1
        ? primary.label
        : approaching > 1
          ? `Approaching ${approaching} limits`
          : primary.label;

  return (
    <button
      type="button"
      onClick={() => onNavigate("/settings")}
      className={cn(
        "font-ui block w-full px-5 py-2 text-left leading-5 transition-colors rounded-sm",
        atLimit
          ? "border border-red-200 bg-red-50 text-red-800 hover:bg-red-100"
          : "border border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100",
      )}
      aria-label={`${primary.label}. View usage in settings.`}
    >
      {label}{" • "}
      <span className="underline decoration-current/40 underline-offset-2">
        view usage
      </span>
    </button>
  );
};

const NavRow = ({
  item,
  active,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-current={active ? "page" : undefined}
    aria-label={item.devOnly ? `${item.label} (developers only)` : undefined}
    title={item.devOnly ? "Developers only" : undefined}
    className={cn(
      // Eyebrow register: small caps, tracked, like the section labels elsewhere.
      "font-ui flex w-full items-center px-5 py-[2px] text-left leading-5 transition-colors focus-visible:outline-none focus-visible:text-zinc-900",
      active ? "font-medium text-zinc-900" : "text-zinc-400 hover:text-zinc-800",
    )}
  >
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <span className="truncate">{item.label}</span>
      {item.beta && (
        <span className="text-3xs text-zinc-300">beta</span>
      )}
      {item.devOnly && (
        <span className="text-3xs text-zinc-300">dev</span>
      )}
    </span>
  </button>
);

/**
 * Persistent left rail. Styled as a quiet contents page rather than a
 * product toolbar, so it sits in the same register as the editorial pages.
 */
export const WorkspaceSidebar = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const closeLeftSidebar = useSetAtom(closeLeftSidebarAtom);
  const { user } = useAuth();

  const go = (to: string) => {
    navigate(to);
    closeLeftSidebar();
  };

  const navItems = useNavItems();
  const { flags } = useFeatureFlags();


  const matchLength = (item: NavItem) =>
    Math.max(
      0,
      ...[item.to, ...(item.matchPrefixes ?? [])]
        .filter((p) => pathname === p || pathname.startsWith(`${p}/`))
        .map((p) => p.length),
    );
  // The most specific item wins when two items share a prefix.
  const isActive = (item: NavItem) => {
    const length = matchLength(item);
    return length > 0 && navItems.every((other) => matchLength(other) <= length);
  };

  const onRepoPath = pathname === "/repos" || pathname.startsWith("/repos/");
  const reposActive = showRepositories(flags) && onRepoPath;
  const clipboardActive =
    pathname.startsWith("/clipboard") || (onRepoPath && !showRepositories(flags));
  const docsActive = pathname === "/docs" || pathname.startsWith("/docs/");
  const chatActive = pathname === "/" || pathname === "/chat" || pathname.startsWith("/chat/");
  const datasets = datasetsAccess(flags, IS_DEV);
  const onDatasets = pathname === DATASETS_ROUTE || pathname.startsWith(`${DATASETS_ROUTE}/`);
  const legacyActive = pathname === LEGACY_DATASETS_ROUTE;
  const fastActive =
    onDatasets && !legacyActive && (datasets.builder || pathname.startsWith("/datasets/shared/"));
  const curationActive = onDatasets && !fastActive;
  const { inspectedDoc } = useTreeViewOutletContext();

  return (
    <div className="flex h-full flex-col bg-white">
      <nav className="shrink-0 pt-5" aria-label="Primary">
        {navItems.map((item) => (
          <NavRow
            key={item.to}
            item={item}
            active={isActive(item)}
            onClick={() => go(item.to)}
          />
        ))}
      </nav>

      {reposActive && user ? (
        <div className="mt-4 flex min-h-0 flex-1 flex-col border-t border-zinc-200/70 pt-3">
          <RepositoriesSidebarSection onNavigate={closeLeftSidebar} />
        </div>
      ) : clipboardActive ? (
        <div
          className={cn(
            "mt-4 flex min-h-0 flex-1 flex-col border-t border-zinc-200/70 pt-2",
            inspectedDoc && "border-b",
          )}
        >
          <div className="min-h-0 flex-1 overflow-y-auto">
            <ClipboardSidebarSections />
          </div>
        </div>
      ) : docsActive ? (
        <div className="mt-4 flex min-h-0 flex-1 flex-col border-y border-zinc-200/70 pt-2">
          <div className="min-h-0 flex-1 overflow-y-auto">
            <DocsSidebarSection />
          </div>
        </div>
      ) : chatActive && user ? (
        <div className="mt-4 flex min-h-0 flex-1 flex-col border-t border-zinc-200/70 pt-3">
          <div className="font-ui flex shrink-0 items-center justify-between px-5 pb-2 text-zinc-400">
            <span>Sessions</span>
            <button
              type="button"
              onClick={() => go("/")}
              aria-label="New session"
              title="New session"
              className="font-ui text-zinc-400 transition-colors hover:text-zinc-800 focus-visible:outline-none focus-visible:text-zinc-900"
            >
              <Plus className="h-4 w-4" aria-hidden />
            </button>
          </div>
          <div className="min-h-0 flex-1">
            <ChatSessionList
              scope="paperclip"
              routeBase="/chat"
              onNavigate={closeLeftSidebar}
              showNewButton={false}
              showRecentsHeader={false}
            />
          </div>
        </div>
      ) : fastActive && user ? (
        <div className="mt-4 flex min-h-0 flex-1 flex-col border-t border-zinc-200/70 pt-3">
          <div className="font-ui flex shrink-0 items-center justify-between px-5 pb-2 text-zinc-400">
            <span>Datasets</span>
            <button
              type="button"
              onClick={() => go(FAST_ROUTE)}
              aria-label="New dataset"
              title="New dataset"
              className="font-ui text-zinc-400 transition-colors hover:text-zinc-800 focus-visible:outline-none focus-visible:text-zinc-900"
            >
              <Plus className="h-4 w-4" aria-hidden />
            </button>
          </div>
          <div className="min-h-0 flex-1">
            <FastDatasetList onNavigate={closeLeftSidebar} />
          </div>
          {datasets.curator === "legacy" && <OldDatasetsLink onNavigate={closeLeftSidebar} />}
        </div>
      ) : curationActive && user ? (
        <div className="mt-4 flex min-h-0 flex-1 flex-col border-t border-zinc-200/70 pt-3">
          <div className="font-ui flex shrink-0 items-center justify-between px-5 pb-2 text-zinc-400">
            <span>{legacyActive ? "Old datasets" : "Datasets"}</span>
            {!legacyActive && (
              <button
                type="button"
                onClick={() => go(DATASETS_ROUTE)}
                aria-label="New dataset"
                title="New dataset"
                className="font-ui text-zinc-400 transition-colors hover:text-zinc-800 focus-visible:outline-none focus-visible:text-zinc-900"
              >
                <Plus className="h-4 w-4" aria-hidden />
              </button>
            )}
          </div>
          <div className="min-h-0 flex-1">
            <CurationSessionList
              onNavigate={closeLeftSidebar}
              routeBase={legacyActive ? LEGACY_DATASETS_ROUTE : DATASETS_ROUTE}
              archived={legacyActive}
            />
          </div>
        </div>
      ) : (
        <div className="flex-1" />
      )}

      <div className="shrink-0 space-y-3 px-5 py-4">
        <ThemeToggle />
        {user && <UsageRailNote onNavigate={go} />}
        {user ? (
          <AccountMenu
            align="start"
            side="top"
            trigger={
              <button
                type="button"
                className="flex w-full items-center gap-2.5 py-1 text-left"
                aria-label="Account menu"
                title="Account"
              >
                <Avatar user={user} size="sm" initialsOnly />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-zinc-800">
                    {user.full_name || user.email}
                  </span>
                  {user.full_name && user.email && (
                    <span className="block truncate text-2xs text-zinc-400">
                      {user.email}
                    </span>
                  )}
                </span>
                <ChevronsUpDown className="h-3 w-3 shrink-0 text-zinc-300" aria-hidden />
              </button>
            }
          />
        ) : (
          <button
            type="button"
            onClick={() => go("/login")}
            className="font-ui text-zinc-400 transition-colors hover:text-zinc-800 focus-visible:outline-none focus-visible:text-zinc-900"
          >
            Sign in
          </button>
        )}
      </div>
    </div>
  );
};
